// Fully Responsive Chakra UI Dashboard
import React, { useState, useEffect } from "react";
import Layout from "../../layout/default";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  IconButton,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Image,
  useToast,
  Stack,
  Badge,
  VStack,
  HStack,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import { CalendarIcon } from "@chakra-ui/icons";
import { MdLocationOn, MdGroups, MdLabel } from "react-icons/md";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./CalendarStyle.css";
import {
  convertDateIntoIndianFormat,
  convertTime,
} from "../../utilities/dateHelper.js";
import moment from "moment-timezone";
import axios from "axios";
import { Select } from "@chakra-ui/react";
import { Skeleton, SkeletonText } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { FormControl, FormLabel } from "@chakra-ui/react";
import sportImg from "../../assets/images/mask/running.png";
import StatusModal from "../../components/StatusModal/StatusModal";
import { jwtDecode } from "jwt-decode";
import Seo, { schema } from "../../utilities/Seo";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const Dashboard = () => {
  const [selectedDates, setSelectedDates] = useState([new Date()]);
  const [allBookings, setAllBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState({});
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState();
  const [calendarKey, setCalendarKey] = useState(0);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalType, setStatusModalType] = useState("");

  const token = sessionStorage.getItem("admintoken")?.split(" ")[1] || "";
  const navigate = useNavigate();


  const handleDay = (action) => {
    const current = selectedDates[0];
    if (action === "prev") current.setDate(current.getDate() - 1);
    if (action === "next") current.setDate(current.getDate() + 1);
    if (action === "today") {
      setSelectedDates([new Date()]);
      setCalendarKey(prev => prev + 1);
    }
    else setSelectedDates([new Date(current)]);
  };

  const handleDateChange = (date) => {
    setSelectedDates([date]);
  };

  const getLatestBookings = async () => {
    try {
      setLoading(true);

      const date = moment(selectedDates[0]).format("YYYY-MM-DD");

      // Prepare payload - only include coachId if a specific coach is selected
      const payload = { date };
      if (selectedCoach && selectedCoach.trim() !== "") {
        payload.coachId = selectedCoach;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/academy/dashboard`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = response.data;

      if (result?.responseCode === 0 && result?.status === "success") {
        setAllBookings(result.data);
        setSelectedBooking(result.data?.[0] || {});
      } else {
        toast({ title: "Failed to fetch coaches", status: "error" });
      }

      setLoading(false);
    } catch (err) {
      console.error(err);
      toast({ title: "Failed to load bookings", status: "error" });
      setLoading(false);
    }
  };

  const fetchCoachData = async () => {
    try {
      const response = await axios({
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_BASE_URL}/api/coach`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setCoaches(response.data.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching coach data:", error);
      throw error;
    }
  };

  const fetchAcademyDetails = async (academyId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/academy/${academyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching academy details:", error);
      return null;
    }
  };

  const checkUserStatus = async () => {
    try {
      const fullToken = sessionStorage.getItem("admintoken");
      if (!fullToken) {
        navigate("/login");
        return;
      }

      const decodedToken = jwtDecode(token);
      const user = decodedToken;
      const academy = user?.academyId;

      if (user?.status === "inactive" || academy?.status === "inactive") {
        setStatusModalType("inactive");
        setShowStatusModal(true);
        return;
      }

      const academyDetails = await fetchAcademyDetails(academy?._id || academy?.id);
      if (!academyDetails?.panNumber || academyDetails?.panNumber?.trim() === "") {
        setStatusModalType("kyc_pending");
        setShowStatusModal(true);
        return;
      }

    } catch (error) {
      console.error("Error checking user status:", error);
      navigate("/login");
    }
  };

  const getDerivedStatus = (item) => {
    if (item.status === "completed") return { status: "Completed", colorScheme: "green" };
    if (item.status === "cancelled") return { status: "Cancelled", colorScheme: "red" };
    if (item.status === "rescheduled") return { status: "Rescheduled", colorScheme: "blue" };

    if (item.status === "upcoming") {
      const now = moment.tz("Asia/Kolkata");
      const sessionDateStr = moment(selectedDates[0]).format("YYYY-MM-DD");
      
      const startTime = item.startTime;
      const endTime = item.endTime;

      if (startTime && endTime) {
        const sessionStart = moment.tz(`${sessionDateStr}T${startTime}`, "Asia/Kolkata");
        const sessionEnd = moment.tz(`${sessionDateStr}T${endTime}`, "Asia/Kolkata");

        if (now.isAfter(sessionEnd)) {
          return { status: "Completed", colorScheme: "green" };
        } else if (now.isSameOrAfter(sessionStart) && now.isSameOrBefore(sessionEnd)) {
          return { status: "Ongoing", colorScheme: "orange" };
        } else {
          return { status: "Upcoming", colorScheme: "yellow" };
        }
      }
    }

    return { status: "Upcoming", colorScheme: "yellow" };
  };

  useEffect(() => {
    const initializeDashboard = async () => {
      await checkUserStatus();
      fetchCoachData();
    };
    initializeDashboard();
  }, []);

  useEffect(() => {
    if (!showStatusModal) {
      getLatestBookings();
    }
  }, [selectedDates, selectedCoach, showStatusModal]);

  return (
    <>
      <Seo
        title="Dashboard | KhelSport Academy"
        description="Academy dashboard to manage bookings, view schedules, and track coaching activities. Monitor daily activities and upcoming sessions."
        canonical="https://khelcoach.com/dashboard"
        url="https://khelcoach.com/dashboard"
        jsonLd={schema.organization({
          name: 'KhelSport Academy Dashboard',
          url: 'https://khelcoach.com/dashboard',
          sameAs: ['https://khelcoach.com']
        })}
      />
      <StatusModal 
        isOpen={showStatusModal} 
        type={statusModalType} 
        onClose={() => setShowStatusModal(false)} 
      />
      <Layout title="Dashboard">
      {/* Page Heading and Back Button */}
      <Flex alignItems="center" gap={0} mb={3}>
        <Button
          variant="ghost"
          size={{ base: "xs", md: "sm" }}
          leftIcon={<IoArrowBackCircleOutline fontSize={"24px"} />}
          onClick={() => navigate(-1)}
          _hover={{ bg: "gray.100" }}
          color="gray.700"
          fontWeight="bold"
        ></Button>
        <Heading
          as="h1"
          fontSize={{ base: "md", md: "lg" }}
          color="gray.700"
          fontWeight="medium"
        >
          Dashboard
        </Heading>
      </Flex>
      <Box
        px={{ base: 0, md: 4, lg: 6 }}
        py={{ base: 2, md: 4 }}
        maxW="100%"
        overflow="hidden"
      >
        {/* Header Controls - Responsive */}
        <Flex
          py={{ base: 2, md: 4 }}
          justifyContent="space-between"
          align="center"
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 0 }}
        >
          <Flex
            align="center"
            gap={{ base: 2, md: 4 }}
            wrap="wrap"
            justify={{ base: "center", md: "flex-start" }}
          >
            <IconButton
              icon={<ChevronLeftIcon />}
              aria-label="Previous day"
              onClick={() => handleDay("prev")}
              size={{ base: "sm", md: "md" }}
            />
            <IconButton
              icon={<ChevronRightIcon />}
              aria-label="Next day"
              onClick={() => handleDay("next")}
              size={{ base: "sm", md: "md" }}
            />
            <Button
              onClick={() => handleDay("today")}
              size={{ base: "sm", md: "md" }}
            >
              Today
            </Button>
            <Text
              fontWeight="semibold"
              fontSize={{ base: "sm", md: "md", lg: "lg" }}
              textAlign={{ base: "center", md: "left" }}
            >
              {`${selectedDates[0].getDate()} ${months[selectedDates[0].getMonth()]
                } ${selectedDates[0].getFullYear()}`}
            </Text>
          </Flex>
        </Flex>

        {/* Main Content - Responsive Layout */}
        <Flex
          direction={{ base: "column", lg: "row" }}
          gap={{ base: 4, md: 6 }}
          align="stretch"
        >
          {/* Left Panel - Calendar & Bookings */}
          <Box
            flex={{ base: "1", lg: "0 0 45%" }}
            bg="white"
            borderRadius="md"
            boxShadow="md"
            p={{ base: 3, md: 4 }}
            minH={{ base: "auto", lg: "80vh" }}
          >
            {/* Calendar Container */}
            <Box
              mb={{ base: 4, md: 6 }}
              sx={{
                ".react-calendar": {
                  width: "100%",
                  border: "none",
                  fontSize: { base: "12px", md: "14px", lg: "16px" },
                },
                ".react-calendar__tile": {
                  height: { base: "40px", md: "55px", lg: "70px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                ".react-calendar__month-view__weekdays": {
                  fontSize: { base: "10px", md: "12px", lg: "14px" },
                  height: { base: "35px", md: "45px", lg: "55px" },
                },
                ".react-calendar__navigation": {
                  height: { base: "40px", md: "55px", lg: "70px" },
                  marginBottom: { base: "0.5rem", md: "1rem" },
                },
                ".react-calendar__navigation button": {
                  fontSize: { base: "14px", md: "16px", lg: "18px" },
                  minWidth: { base: "30px", md: "40px", lg: "50px" },
                },
              }}
            >
              <Calendar
                key={calendarKey}
                value={selectedDates}
                onChange={handleDateChange}
                tileClassName={() => "chakra-calendar-tile"}
              />
            </Box>

            {/* Upcoming Section Header */}
            <Flex
              direction="row"
              align="center"
              justify="space-between"
              mb={{ base: 3, md: 4 }}
              gap={{ base: 2, md: 0 }}
            >
              <Text
                fontSize={{ base: "md", md: "lg", lg: "xl" }}
                fontWeight="bold"
              >
                Upcoming
              </Text>

              <FormControl maxW={{ base: "160px", md: "200px" }}>
                <FormLabel fontSize={{ base: "sm", md: "md" }}>Select Coach</FormLabel>
                <Select
                  placeholder="All"
                  size={{ base: "sm", md: "md" }}
                  onChange={(x) => setSelectedCoach(x.target.value)}
                >
                  {coaches &&
                    coaches.length > 0 &&
                    coaches.map((x) => (
                      <option key={x._id} value={x._id}>
                        {x.firstName + " " + x.lastName}
                      </option>
                    ))}
                </Select>
              </FormControl>
            </Flex>

            {/* Bookings List */}
            <Box
              maxH={{ base: "40vh", md: "50vh", lg: "52vh" }}
              overflowY="auto"
              css={{
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
              }}
            >
              {allBookings.length > 0 ? (
                <VStack spacing={{ base: 2, md: 3 }} align="stretch">
                  {allBookings.map((booking, idx) => (
                    <Box
                      key={idx}
                      borderWidth="1px"
                      borderRadius="md"
                      overflow="hidden"
                      onClick={() => setSelectedBooking(booking)}
                      bg={booking === selectedBooking ? "blue.50" : "white"}
                      p={{ base: 3, md: 4 }}
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      transition="background-color 0.2s"
                    >
                      <VStack align="stretch" spacing={2}>
                        <Text
                          fontWeight="medium"
                          fontSize={{ base: "sm", md: "md" }}
                          noOfLines={2}
                        >
                          {booking.name.length > 50
                            ? `${booking.name.slice(0, 50)}...`
                            : booking.name}
                        </Text>
                        <HStack
                          fontSize={{ base: "xs", md: "sm" }}
                          color="gray.500"
                          spacing={1}
                        >
                          <CalendarIcon />
                          <Text>
                            {convertDateIntoIndianFormat(selectedDates[0])}
                            &nbsp; at &nbsp;
                            {`${convertTime(
                              booking?.startTime
                            )} - ${convertTime(booking?.endTime)} `}
                            <Text as="span" fontSize="sm">IST</Text>
                          </Text>
                        </HStack>
                        <HStack justify="flex-end" spacing={2}>
                          {booking.type === "class" && (
                            <Badge
                              colorScheme="green"
                              size={{ base: "sm", md: "md" }}
                            >
                              Class
                            </Badge>
                          )}
                          {booking.type === "course" && (
                            <Badge
                              colorScheme="yellow"
                              size={{ base: "sm", md: "md" }}
                            >
                              Course
                            </Badge>
                          )}
                          {booking.type === "event" && (
                            <Badge
                              colorScheme="blue"
                              size={{ base: "sm", md: "md" }}
                            >
                              Break
                            </Badge>
                          )}
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text
                  textAlign="center"
                  color="gray.500"
                  py={{ base: 4, md: 8 }}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  No Bookings Found
                </Text>
              )}
            </Box>
          </Box>

          {/* Right Panel - Booking Details */}
          <Box
            flex={{ base: "1", lg: "0 0 50%" }}
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="md"
            boxShadow="sm"
            minH={{ base: "300px", lg: "80vh" }}
          >
            {loading ? (
              <Stack spacing={4} py={{ base: 4, md: 10 }}>
                <Skeleton height="60px" borderRadius="md" />
                <SkeletonText noOfLines={3} spacing="4" skeletonHeight="2" />
                <Skeleton height="40px" borderRadius="md" />
                <SkeletonText noOfLines={2} spacing="4" skeletonHeight="2" />
              </Stack>
            ) : selectedBooking.name ? (
              <Stack spacing={{ base: 3, md: 4 }}>
                <Flex
                  justify="space-between"
                  align="flex-start"
                  direction={{ base: "column", md: "row" }}
                  gap={{ base: 3, md: 0 }}
                >
                  {/* Left - Image + Info */}
                  <HStack
                    spacing={{ base: 3, md: 4 }}
                    align="flex-start"
                    flex="1"
                  >
                    <Image
                      boxSize={{ base: "50px", md: "70px" }}
                      objectFit="cover"
                      borderRadius="md"
                      src={selectedBooking.image || sportImg}
                      alt="Course"
                    />
                    <VStack align="flex-start" spacing={1} flex="1">
                      <Text
                        fontWeight="semibold"
                        fontSize={{ base: "sm", md: "md" }}
                        noOfLines={2}
                      >
                        {selectedBooking.name}
                      </Text>
                      <Text
                        fontWeight="medium"
                        color="blue.600"
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        {convertTime(selectedBooking.startTime)} -{" "}
                        {convertTime(selectedBooking.endTime)} 
                        <Text as="span" fontSize="sm"> IST</Text>
                      </Text>
                      <HStack
                        color="gray.600"
                        fontSize={{ base: "xs", md: "sm" }}
                        spacing={1}
                      >
                        <MdLocationOn />
                        <Text>
                          {selectedBooking?.facility
                            ? selectedBooking.facility
                            : "No location provided"}
                        </Text>
                      </HStack>

                    </VStack>
                  </HStack>

                  {/* Right - Date, Count, Refresh */}
                  <VStack
                    textAlign="right"
                    fontSize={{ base: "xs", md: "sm" }}
                    spacing={2}
                    align={{ base: "stretch", md: "flex-end" }}
                  >
                    <Button
                      size={{ base: "xs", md: "sm" }}
                      variant="ghost"
                      colorScheme="green"
                      leftIcon={<RepeatIcon />}
                      onClick={getLatestBookings}
                    >
                      Refresh
                    </Button>
                    <Text>{convertDateIntoIndianFormat(selectedDates[0])}</Text>
                    <Text color="gray.500">
                      No. of Bookings: {selectedBooking?.bookings?.length || 0}
                    </Text>
                  </VStack>
                </Flex>

                {/* Bookings List */}
                <Box
                  borderTop="1px solid"
                  borderColor="gray.100"
                  pt={{ base: 3, md: 4 }}
                >

                  {selectedBooking?.bookings?.length > 0 ? (
                    <Stack spacing={{ base: 2, md: 3 }}>
                      {selectedBooking.bookings.map((item, idx) => (
                        <Box
                          key={idx}
                          p={{ base: 4, md: 6 }}
                          bg="white"
                          borderRadius="md"
                          boxShadow="sm"
                          border="1px"
                          borderColor="gray.200"
                          _hover={{
                            boxShadow: "md",
                            transform: "translateY(-1px)",
                            transition: "all 0.2s",
                          }}
                        >
                          <Flex justify="space-between" align="flex-start">
                            <Box flex="1">
                              <Text
                                fontWeight="bold"
                                fontSize={{ base: "md", md: "lg" }}
                                color="gray.800"
                                mb={2}
                              >
                                {item.name}
                              </Text>
                              <Flex align="center" gap={{ base: 4, md: 6 }}>
                                <Box>
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    fontWeight="medium"
                                    mb={1}
                                  >
                                    START TIME
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    color="gray.700"
                                    fontWeight="semibold"
                                  >
                                    {(() => {
                                      if (!item.startTime) return "n/a";
                                      const [hours, minutes] = item.startTime.split(":");
                                      const hour12 = hours % 12 || 12;
                                      const ampm = hours >= 12 ? "PM" : "AM";
                                      return `${hour12}:${minutes} ${ampm}`;
                                    })()}
                                  </Text>
                                </Box>
                                <Box>
                                  <Text
                                    fontSize="xs"
                                    color="gray.500"
                                    fontWeight="medium"
                                    mb={1}
                                  >
                                    END TIME
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    color="gray.700"
                                    fontWeight="semibold"
                                  >
                                    {(() => {
                                      if (!item.endTime) return "n/a";
                                      const [hours, minutes] = item.endTime.split(":");
                                      const hour12 = hours % 12 || 12;
                                      const ampm = hours >= 12 ? "PM" : "AM";
                                      return `${hour12}:${minutes} ${ampm}`;
                                    })()}
                                  </Text>
                                </Box>
                              </Flex>
                            </Box>
                            <Flex align="center" gap={3}>
                              {(() => {
                                const bookingStatusMap = {
                                  completed: { label: 'Completed', color: 'green' },
                                  cancelled: { label: 'Cancelled', color: 'red' },
                                  rescheduled: { label: 'Rescheduled', color: 'blue' },
                                };
                            <Text as="span" fontSize="xs" color="gray.500">
                              IST
                            </Text>
                                const bookingRescheduled =
                                  item.status === "rescheduled" ||
                                  item.isRescheduled === true ||
                                  item.rescheduled === true ||
                                  (item.reschedule && (
                                    item.reschedule === true ||
                                    item.reschedule.status === "rescheduled"
                                  ));

                                const bookingBadge = bookingRescheduled
                                  ? { label: 'Rescheduled', color: 'blue' }
                                  : bookingStatusMap[item.status];

                                const now = moment.tz('Asia/Kolkata');
                                const sessionDateStr = moment(selectedDates[0]).format('YYYY-MM-DD');
                                let timelineLabel = 'Upcoming';
                                let timelineColor = 'yellow';

                                if (item.startTime && item.endTime) {
                                  const sessionStart = moment.tz(`${sessionDateStr}T${item.startTime}`, 'Asia/Kolkata');
                                  const sessionEnd = moment.tz(`${sessionDateStr}T${item.endTime}`, 'Asia/Kolkata');

                                  if (now.isAfter(sessionEnd)) {
                                    timelineLabel = 'Completed';
                                    timelineColor = 'green';
                                  } else if (now.isSameOrAfter(sessionStart) && now.isSameOrBefore(sessionEnd)) {
                                    timelineLabel = 'Ongoing';
                                    timelineColor = 'orange';
                                  } else {
                                    timelineLabel = 'Upcoming';
                                    timelineColor = 'yellow';
                                  }
                                }

                                const showTimelineBadge = !(bookingBadge && bookingBadge.label === 'Cancelled');

                                return (
                                  <Flex gap={2} align="center">
                                    {bookingBadge && (
                                      <Badge
                                        colorScheme={bookingBadge.color}
                                        variant="subtle"
                                        borderRadius="full"
                                        px={3}
                                        py={1}
                                        fontSize="xs"
                                      >
                                        {bookingBadge.label}
                                      </Badge>
                                    )}
                                    {showTimelineBadge && (
                                      <Badge
                                        colorScheme={timelineColor}
                                        variant="subtle"
                                        borderRadius="full"
                                        px={3}
                                        py={1}
                                        fontSize="xs"
                                      >
                                        {timelineLabel}
                                      </Badge>
                                    )}
                                  </Flex>
                                );
                              })()}
                            </Flex>
                          </Flex>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Text
                      textAlign="center"
                      color="gray.500"
                      py={{ base: 4, md: 8 }}
                      fontSize={{ base: "sm", md: "md" }}
                    >
                      No bookings found for this selection
                    </Text>
                  )}
                </Box>
              </Stack>
            ) : (
              <Text
                textAlign="center"
                color="gray.500"
                py={{ base: 4, md: 8 }}
                fontSize={{ base: "sm", md: "md" }}
              >
                No Booking Selected
              </Text>
            )}
          </Box>
        </Flex>
      </Box >
    </Layout>
    </>
  );
};

export default Dashboard;
